import React from 'react';
import {FormattedMessage, injectIntl} from "react-intl";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Tooltip from "@amzn/meridian/tooltip";
import Icon from "@amzn/meridian/icon";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import { INBOUND_STRINGS, STOW_WIP_LOWER, STOW_WIP_UPPER, WIP_TARGET, STOW_WIP_PRECISION } from "../util/Constants";

/**
 * Stow Metrics Overview Card.
 * Renders a Meridian Box with title, tooltip, Target column and Actual column.
 */
const StowMetricOverviewCard = ({title, tooltipText, targetLabel, targetValue, actualValue, isAlert}) => {
    return (
        <Box type="outline" spacingInset="400">
            <div>
                <div>
                    <Heading level={2} type="h300" alignment="left">
                        {title}
                        {tooltipText && (
                            <Tooltip position="right" title={tooltipText}>
                                <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
                            </Tooltip>
                        )}
                    </Heading>
                </div>
                <Row width="100%" widths={["grid-6", "grid-6"]}>
                    <div>
                        <Text type="b100">{targetLabel}</Text>
                        <Text type="d50">{targetValue}</Text>
                    </div>
                    <div>
                        <Text type="b100">
                            <FormattedMessage id="stow.actual" defaultMessage="Actual" />
                        </Text>
                        <Text type="d50" className={isAlert ? "alert" : ""}>
                            {actualValue}
                        </Text>
                    </div>
                </Row>
            </div>
        </Box>
    );
};

/**
 * Stow Metrics Section — displays Stow FPH, Est. Stow WIP Compliance, and Stow WIP
 * cards below the existing Station Performance Summary cards.
 * Data is fetched for CYCLE_1 only (hardcoded).
 */
const StowMetricsSection = ({stowMetrics, stowMetricsError, intl}) => {
    // Don't render anything during initial loading state
    if (!stowMetrics && !stowMetricsError) {
        return null;
    }

    var stowFphActual = stowMetrics && stowMetrics.stowFph != null ? Math.round(stowMetrics.stowFph) : 0;
    var stowFphTarget = stowMetrics && stowMetrics.stowFphTarget != null ? Math.round(stowMetrics.stowFphTarget) : 0;
    var stowWipActual = stowMetrics && stowMetrics.stowWip != null ? stowMetrics.stowWip : 0;
    var stowWipLower = stowMetrics && stowMetrics.stowWipLower != null ? stowMetrics.stowWipLower : STOW_WIP_LOWER;
    var stowWipUpper = stowMetrics && stowMetrics.stowWipUpper != null ? stowMetrics.stowWipUpper : STOW_WIP_UPPER;
    var stowWipComplianceActual = stowMetrics && stowMetrics.stowWipCompliance != null ? stowMetrics.stowWipCompliance : 0;
    var stowWipComplianceTarget = stowMetrics && stowMetrics.stowWipComplianceTarget != null ? stowMetrics.stowWipComplianceTarget : WIP_TARGET;
    var isWipOutOfRange = stowMetrics != null && (stowWipActual < stowWipLower || stowWipActual > stowWipUpper);
    var isComplianceBelowTarget = stowMetrics != null && (stowWipComplianceActual < stowWipComplianceTarget);

    return (
        <div>
            <h2 className="stow-metrics-title" style={{marginTop: '32px'}}>
                <FormattedMessage id="inbound.stow_metrics" defaultMessage="Stow Metrics"/>
                <Tooltip position="right" title={intl.formatMessage({id: INBOUND_STRINGS.TOOLTIP_STOW_METRICS_SECTION})}>
                    <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
                </Tooltip>
                <span style={{fontSize: '14px', fontWeight: 400, color: '#6B6B6B', marginLeft: '12px'}}>
                    (CYCLE_1)
                </span>
            </h2>
            {stowMetricsError ? (
                <div className="error-message" style={{marginBottom: '16px'}}>
                    <FormattedMessage id="inbound.stow_metrics_error" defaultMessage="Unable to load stow metrics"/>
                </div>
            ) : (
                <Row widths={["grid-4", "grid-4", "grid-4"]} wrap="down" alignmentVertical="stretch">
                    <StowMetricOverviewCard
                        title={<FormattedMessage id="stow.stow_fph" defaultMessage="Stow FPH"/>}
                        tooltipText={intl.formatMessage({id: INBOUND_STRINGS.TOOLTIP_STOW_FPH})}
                        targetLabel={<FormattedMessage id="stow.target" defaultMessage="Target"/>}
                        targetValue={stowFphTarget.toLocaleString()}
                        actualValue={stowFphActual.toLocaleString()}
                        isAlert={stowFphActual < stowFphTarget}
                    />
                    <StowMetricOverviewCard
                        title={<FormattedMessage id="stow.stow_wip_compliance" defaultMessage="Est. Stow WIP Compliance"/>}
                        tooltipText={intl.formatMessage({id: INBOUND_STRINGS.TOOLTIP_STOW_WIP_COMPLIANCE})}
                        targetLabel={<FormattedMessage id="stow.target" defaultMessage="Target"/>}
                        targetValue={stowWipComplianceTarget.toFixed(STOW_WIP_PRECISION) + "%"}
                        actualValue={stowWipComplianceActual.toFixed(STOW_WIP_PRECISION) + "%"}
                        isAlert={isComplianceBelowTarget}
                    />
                    <StowMetricOverviewCard
                        title={<FormattedMessage id="stow.stow_wip" defaultMessage="Stow WIP"/>}
                        tooltipText={intl.formatMessage({id: INBOUND_STRINGS.TOOLTIP_STOW_WIP})}
                        targetLabel={<FormattedMessage id="stow.target_min_max" defaultMessage="Target (min/max)"/>}
                        targetValue={stowWipLower.toFixed(STOW_WIP_PRECISION) + "/" + stowWipUpper.toFixed(STOW_WIP_PRECISION)}
                        actualValue={stowWipActual.toFixed(STOW_WIP_PRECISION)}
                        isAlert={isWipOutOfRange}
                    />
                </Row>
            )}
        </div>
    );
};

export default injectIntl(StowMetricsSection);



// WEBPACK FOOTER //
// ./src/components/inbound/StowMetricsSection.js